// ============================================================
//  Editable content — the only file you need to touch to make
//  the words feel truly *yours*. Swap the placeholders below.
//
//  Identifiers here are English like the rest of the shared package, but every
//  string is the real Portuguese copy shown in the app and is left untouched.
// ============================================================

export const couple = {
  nameOne: "Nuno",
  nameTwo: "Catarina",
  /** Your "official" date (YYYY-MM-DD) — used in the affectionate messages. */
  officialDate: "2025-06-21",
};

/**
 * The other one of you, by name.
 *
 * The app used to say "ela" and "a outra pessoa" wherever it meant your other
 * half. Both were written from one side of the couple: logged in as Catarina,
 * "A palavra dela" pointed at Nuno and read as nonsense.
 *
 * Naming the person fixes it in the way that needs no grammar at all — no
 * pronoun to agree with anything, and "A palavra de Catarina" is warmer than
 * "A palavra da outra pessoa" besides. Deliberately no article ("de", not
 * "da"/"do"): the article is the one part of the phrase that would still have
 * to know a gender, and it buys nothing.
 *
 * `me` is the logged-in person's name or email. Anything unrecognised falls
 * back to nameTwo, so a stray account never renders "undefined" at someone.
 */
export function partnerName(me: string | null | undefined): string {
  if (!me) return couple.nameTwo;

  const mine = me.trim().toLowerCase();
  // Matches the display name ("Catarina") or the login ("catarina@…"), because
  // callers have whichever of the two the session happened to give them.
  const isOne = mine === couple.nameOne.toLowerCase() || mine.startsWith(`${couple.nameOne.toLowerCase()}@`);

  return isOne ? couple.nameTwo : couple.nameOne;
}

export const landing = {
  kicker: "Era uma vez…",
  titulo: "A Nossa História",
  subtitulo: "o nosso pequeno conto de fadas, escrito a dois.",
  paragrafos: [
    "Há histórias que merecem mais do que ficar na memória — merecem um lugar só nosso, onde cada momento volta a brilhar.",
    "Este é o nosso baú encantado: a linha do tempo dos nossos marcos e o álbum das nossas fotografias. Um espaço que cresce connosco, página após página.",
  ],
  rodape: "Feito com amor, só para nós.",
};

/**
 * Friendly empty-states, reused across the app.
 *
 * The inner keys stay Portuguese here (and in `landing`/`anniversary` below)
 * because components read them directly as `landing.titulo`, `vazios.album`…
 * Renaming those is a Phase 7 concern, done together with the components.
 */
export const emptyStates = {
  timeline: "A vossa história está só a começar… O primeiro marco do vosso trilho aparece aqui.",
  album: "O baú ainda está à espera das primeiras memórias. Em breve, enche-se de luz.",
};

// ============================================================
//  A SURPRESA DE 21 DE JUNHO — a página secreta de aniversário.
//  ↓↓↓ ESCREVE AQUI a tua carta à Catarina. É só este texto.
//  Cada string em "carta" é um parágrafo.
// ============================================================
export const anniversary = {
  /** Antes do dia: o que aparece se ela encontrar a página mais cedo. */
  teaserKicker: "Há algo à tua espera…",
  teaserTitulo: "Ainda não, meu amor",
  teaserTexto:
    "Esta página guarda uma surpresa que só floresce no nosso dia. Tem só mais um bocadinho de paciência…",

  /** No dia (e depois): o título da cena. {anos} é substituído pelo nº de anos. */
  kicker: "21 de junho",
  // Ex.: "O nosso primeiro aniversário" / "O nosso 2.º aniversário"
  saudacao: "Feliz aniversário, meu amor 💛",

  /** A CARTA — substitui pelos teus verdadeiros sentimentos. */
  carta: [
    "Catarina,",
    "Há um ano que a nossa história deixou de ser minha ou tua para passar a ser nossa. E se eu pudesse escolher outra vez, escolhia-te em todas as versões deste conto.",
    "Precisamente há um ano, que combinamos ir buscar sushi à peixaria neves vir para a tua praia favorita ao final da tarde para apreciar um dos momentos mais belos do dia juntos, o pôr do sol e comer a nossa comida preferida. Nessa altura eu já sabia que queria passar muitos mais anos a viver momentos como esse contigo, a descobrir novos sabores, novas praias, novos pôres do sol, novas memórias.", 
    "No nosso meio ano de aniversário disse-te que eras a minha maior certeza, por seres a minha amiga, melhor amiga, namorada, mulher psicologica, futurar mulher/esposa e futura mãe dos meus filhos. Tambem o digo, por cada dia que te conheço melhor vejo em ti sempre algo mais bonito de ti. A forma como cuidas e gostas de estar com a tua familia. O quanto te esforças para estar presente com todas as pessoas que te são proximas e queridas. A forma como tu estas sempre disponível para ajudar tudo e todos. A tua personalidade de menina de ouro carregada de bondade e esperança que vê sempre o melhor lados nos outros. A mulher carinhosa e amorosa. A mulher que se entrega com tudo que tem no que se compromete. A mulher ambiciosa, empenhada e trabalhadora. Mas mais que tudo isto, um exemplo. E hoje, um ano depois, com tudo que se esta a passar no mundo, eu só tenho uma única certeza. Eu só tenho a certeza de que quero passar o resto da minha vida a viver tudo isso contigo.",
    "Agora após um ano tambem já vivemos muitas coisas bonitas juntos, ja estivemos lá um para o outro nos momentos mais dificeis, ja partilhamos momentos de alegria e felicidade, ja partilhamos momentos de tristeza e dor, ja partilhamos momentos de raiva e frustração, ja partilhamos momentos de amor e carinho. E é por isso que eu sei que quero passar o resto da minha vida contigo, a viver tudo isso contigo. Porque eu vou estar lá para ti em todos os momentos porque eu amo-te em todos eles e quero aprender cada vez mais sobre ti em cada um deles e juntos trabalharmos para sermos cada vez melhores um para o outro. E eu sei que tal como eu vou estar la sempre para ti, tu vais estar lá para mim. Não precisamos de o estar a dizer constantemente porque as nossas ações falam por sí, estamos a construir algo nosso, JUNTOS. Vai ser sempre assim e estou aqui para te dizer isso catarina, há um ano atrás sem saberes assinaste um contrato para a vida toda, com um homem que está completamente apaixonado por ti e encontrou em ti tudo que sempre sonhou.", 
    "Um ano de memórias, de momentos, de conquistas, de alegrias, de tristezas, de desafios, de superações, de aprendizados, de crescimento, de amor. Um ano que passou a correr e que nos trouxe até aqui. E eu não podia estar mais feliz por ter passado este ano contigo e por ter a certeza que quero passar muitos mais anos contigo. Porque tu és a minha vida, a minha felicidade, o meu amor. E eu quero passar o resto da minha vida a fazer-te feliz e a ser feliz contigo.",
    "Para nos ajudar a registar toda a nossa jornada, construí este cantinho para guardar tudo o que somos — cada marco, cada fotografia, cada momento que vivemos juntos para que o nosso 'Era uma vez' tenha um sítio onde ser escrito. Juntos vamos tornar este cantinho o lugar mais lindo para revivermos tudo o que vamos conquistar juntos.", 
    "Mas a verdade é só uma: o lugar mais bonito desta história és tu.",
    "Obrigado por seres o meu para sempre. Que venham muitos mais 21 de junho.",
    "Amo-te, catarina Almeida💛"
  ],
  assinatura: "Para sempre teu, Nuno",

  rodape: "Feito com todo o meu amor.",
};
